/**
 * Batch Contribution Summary adapter.
 *
 * The pure aggregation (void exclusion, NaN guards, method/donor counts, fund
 * rollup, contributor-name resolution, totals) lives in
 * @glorify-llc/accounting-core and is shared with Relay (ADR-006). This file
 * keeps the Forge-specific seam: it injects the metric-registry summary labels in
 * the original 8-item order.
 *
 * Consumers keep importing from "@/lib/reports/adapters/batch-contribution-summary".
 */
import { type ReportAggregation } from "@glorify-llc/accounting-core";
import { buildReportSummaryItem } from "@/lib/metrics/metric-registry";
import {
  aggregateBatchContributionSummary,
  type BatchContributionSummaryContext,
} from "@glorify-llc/accounting-core";

export type {
  BatchContributionSummaryContext,
  BatchContribution,
  BatchAllocation,
} from "@glorify-llc/accounting-core";

export function buildBatchContributionSummaryAggregation(
  context: BatchContributionSummaryContext,
): ReportAggregation {
  const {
    rows,
    batchLabel,
    batchDate,
    contributionCount,
    totalAmount,
    cashCount,
    checkCount,
    namedCount,
    anonymousCount,
  } = aggregateBatchContributionSummary(context);

  return {
    asOf: context.asOf,
    summary: [
      buildReportSummaryItem("report.batch_contribution.batch_label", batchLabel),
      buildReportSummaryItem("report.batch_contribution.batch_date", batchDate),
      buildReportSummaryItem("report.batch_contribution.contribution_count", contributionCount),
      buildReportSummaryItem("report.batch_contribution.total_amount", totalAmount),
      buildReportSummaryItem("report.batch_contribution.cash_count", cashCount),
      buildReportSummaryItem("report.batch_contribution.check_count", checkCount),
      buildReportSummaryItem("report.batch_contribution.named_donors", namedCount),
      buildReportSummaryItem("report.batch_contribution.anonymous_donors", anonymousCount),
    ],
    rows,
  };
}
